import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, Users, FileText, Tag } from 'lucide-react';
import './EditEventModal.css';
import MarkdownEditor from './MarkdownEditor';

interface EditEventModalProps {
  event: {
    id: string;
    title: string;
    description: string;
    theme: string;
    status: string;
    startDate: Date | string;
    endDate: Date | string;
    registrationDeadline: Date | string;
    maxParticipants: number;
  };
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
}

const EditEventModal: React.FC<EditEventModalProps> = ({ event, onClose, onSave }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: event.title || '',
    description: event.description || '',
    theme: event.theme || '',
    status: event.status || 'draft',
    startDate: formatDateForInput(event.startDate),
    endDate: formatDateForInput(event.endDate),
    registrationDeadline: formatDateForInput(event.registrationDeadline),
    maxParticipants: event.maxParticipants || 100,
  });

  function formatDateForInput(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Close on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="edit-event-modal-overlay" onClick={onClose}>
      <div className="edit-event-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="edit-event-modal-header">
          <h2 className="edit-event-modal-title">Edit Event</h2>
          <button
            onClick={onClose}
            className="edit-event-modal-close"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="edit-event-modal-form">
          {/* Basic Information Section */}
          <div className="edit-event-form-section">
            <h3 className="edit-event-section-title">
              <FileText className="h-5 w-5" />
              Basic Information
            </h3>

            <div className="edit-event-form-grid">
              <div className="edit-event-form-group edit-event-form-full">
                <label className="edit-event-form-label">
                  Event Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="edit-event-form-input"
                  placeholder="Enter event title"
                />
              </div>

              <div className="edit-event-form-group edit-event-form-full">
                <label className="edit-event-form-label">
                  Description
                </label>
                <MarkdownEditor
                  value={formData.description}
                  onChange={(value) => handleChange('description', value)}
                  placeholder="Describe the event. Use **bold**, *italic*, [links](url), and - bullet lists."
                  height={200}
                />
              </div>

              <div className="edit-event-form-group">
                <label className="edit-event-form-label">
                  <Tag className="h-4 w-4 inline mr-1" />
                  Theme
                </label>
                <input
                  type="text"
                  value={formData.theme}
                  onChange={(e) => handleChange('theme', e.target.value)}
                  className="edit-event-form-input"
                  placeholder="Event theme"
                />
              </div>

              <div className="edit-event-form-group">
                <label className="edit-event-form-label">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="edit-event-form-select"
                >
                  {formData.status === 'Unknown' && <option value="Unknown">Not Set</option>}
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div className="edit-event-form-section">
            <h3 className="edit-event-section-title">
              <Calendar className="h-5 w-5" />
              Dates & Deadlines
            </h3>

            <div className="edit-event-form-grid">
              <div className="edit-event-form-group">
                <label className="edit-event-form-label">
                  Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className="edit-event-form-input"
                />
              </div>

              <div className="edit-event-form-group">
                <label className="edit-event-form-label">
                  End Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  className="edit-event-form-input"
                />
              </div>

              <div className="edit-event-form-group">
                <label className="edit-event-form-label">
                  Registration Deadline
                </label>
                <input
                  type="date"
                  value={formData.registrationDeadline}
                  onChange={(e) => handleChange('registrationDeadline', e.target.value)}
                  className="edit-event-form-input"
                />
              </div>
            </div>
          </div>

          {/* Participants Section */}
          <div className="edit-event-form-section">
            <h3 className="edit-event-section-title">
              <Users className="h-5 w-5" />
              Participants
            </h3>

            <div className="edit-event-form-grid">
              <div className="edit-event-form-group">
                <label className="edit-event-form-label">
                  Max Participants
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxParticipants}
                  onChange={(e) => handleChange('maxParticipants', parseInt(e.target.value))}
                  className="edit-event-form-input"
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="edit-event-modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="edit-event-btn edit-event-btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edit-event-btn edit-event-btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <span className="edit-event-btn-loading">
                  <div className="edit-event-spinner"></div>
                  Saving...
                </span>
              ) : (
                <span className="edit-event-btn-content">
                  <Save className="h-4 w-4" />
                  Save Changes
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventModal;

